import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AddMeterConversionFactorCommand,
  AddMeterFrameConversionFactorCommand,
  ConversionFactorsService,
  MeterDto,
  MeterFrameDto,
} from '@app/api/nts';
import { FormApiService } from '@app/core/services/form-api.service';
import { TranslatePipe } from '@ngx-translate/core';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { InputNumberDirective } from '@shared/components/input/number/input-number.directive';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { AlertService } from '@app/core/services/alert.service';
import { ToggleComponent } from '@shared/components/input/toggle/toggle.component';
import { Subscription } from 'rxjs';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

const FACTOR_KEYS = ['conversionFactorA14', 'conversionFactorA23', 'conversionFactorR12', 'conversionFactorR34'] as const;

@Component({
  selector: 'app-meterframe-add-conversion-factor',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputGroupComponent,
    LabelDirective,
    SelectDirective,
    InputDirective,
    InputNumberDirective,
    ToggleComponent,
  ],
  templateUrl: './meterframe-add-conversion-factor.component.html',
})
export class MeterframeAddConversionFactorComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  private readonly subscriptions = new Subscription();

  /** Set by ModalService **/
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public meterFrame?: MeterFrameDto;
  public meter?: MeterDto;

  public applyToOptions: KeyValue[] = [];

  /** Mirrors all four factor fields to the same value when enabled */
  public mirrorValues = new FormControl<boolean>(true);

  public form = new FormGroup({
    applyTo:             new FormControl<string | null>(null, Validators.required),
    validFrom:           new FormControl<string | null>(null, Validators.required),
    validTo:             new FormControl<string | null>(null),
    conversionFactorA14: new FormControl<number>(1, Validators.required),
    conversionFactorA23: new FormControl<number>(1, Validators.required),
    conversionFactorR12: new FormControl<number>(1, Validators.required),
    conversionFactorR34: new FormControl<number>(1, Validators.required),
  });

  constructor(
    private readonly conversionFactorsService: ConversionFactorsService,
    private readonly formApiService: FormApiService,
    private readonly alertService: AlertService,
  ) {}

  public ngOnInit(): void {
    this.applyToOptions = [
      { key: 'meterframe', value: 'conversionFactorModal.applyToMeterframe' },
    ];

    if (this.meter?.id) {
      this.applyToOptions.push({ key: 'meter', value: 'conversionFactorModal.applyToMeter' });
    }

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    this.form.get('validFrom')?.setValue(`${yyyy}-${mm}-${dd}T00:00`);

    this.alertService.info('modal', 'conversionFactorModal.recalculationWarning');

    // Mirror: when any factor changes and mirroring is on, sync the other three
    for (const key of FACTOR_KEYS) {
      this.subscriptions.add(
        this.form.get(key)!.valueChanges.subscribe((val) => {
          if (!this.mirrorValues.value) return;
          for (const other of FACTOR_KEYS) {
            if (other === key) continue;
            this.form.get(other)?.setValue(val, { emitEvent: false });
          }
        })
      );
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public submit(): void {
    const applyTo = this.form.get('applyTo')?.value;
    const v = this.form.value;

    const command: AddMeterFrameConversionFactorCommand | AddMeterConversionFactorCommand = {
      conversionFactorA14: v.conversionFactorA14!,
      conversionFactorA23: v.conversionFactorA23!,
      conversionFactorR12: v.conversionFactorR12!,
      conversionFactorR34: v.conversionFactorR34!,
      validFrom: v.validFrom!,
      validTo: v.validTo ?? null,
    };

    if (applyTo === 'meterframe' && this.meterFrame?.id) {
      this.formApiService.submit(
        this.form,
        () => this.conversionFactorsService.meterframesIdConversionFactorsPost(this.meterFrame!.id!, command),
        { successMessage: 'conversionFactorModal.success', closeModal: true },
      );
    } else if (applyTo === 'meter' && this.meter?.id) {
      this.formApiService.submit(
        this.form,
        () => this.conversionFactorsService.metersIdConversionFactorsPost(this.meter!.id!, command),
        { successMessage: 'conversionFactorModal.success', closeModal: true },
      );
    }
  }
}
